import React, {useEffect, useState} from 'react';
import Modal from '../workspace/Modal';
import {Project} from '../../domain/types';
import {controlClass} from '../formStyles';

export interface ProjectSubmission {
  name: string;
  description: string;
}

interface ProjectFormModalProps {
  isOpen: boolean;
  /** Create and edit ask for the same two things, so they are one dialog. */
  mode: 'create' | 'edit';
  project: Project | null;
  isSaving: boolean;
  onClose: () => void;
  onSubmit: (submission: ProjectSubmission) => void;
}

const ProjectFormModal: React.FC<ProjectFormModalProps> = ({
  isOpen,
  mode,
  project,
  isSaving,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setName(mode === 'edit' ? project?.name ?? '' : '');
    setDescription(mode === 'edit' ? project?.description ?? '' : '');
  }, [isOpen, mode, project]);

  if (!isOpen || (mode === 'edit' && !project)) {
    return null;
  }

  const canSave = name.trim().length > 0 && !isSaving;

  const submit = () => {
    if (!canSave) {
      return;
    }
    onSubmit({name: name.trim(), description: description.trim()});
  };

  return (
    <Modal
      isOpen
      title={mode === 'create' ? 'New project' : 'Edit project'}
      subtitle={
        mode === 'create'
          ? 'Audio, transcriptions and a default word list, kept together. Renaming it later is fine.'
          : 'Name and description are synced to your account, so every device sees the change.'
      }
      onClose={isSaving ? () => {} : onClose}
      widthClass="max-w-lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-800 hover:text-gray-100
             transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSave}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white
             hover:bg-indigo-500 transition-colors disabled:opacity-40 disabled:cursor-not-allowed
             cursor-pointer"
          >
            {isSaving
              ? (mode === 'create' ? 'Creating…' : 'Saving…')
              : (mode === 'create' ? 'Create project' : 'Save changes')}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label htmlFor="project-name" className="mb-1 block text-xs font-medium text-gray-300">
            Name
          </label>
          <input
            id="project-name"
            type="text"
            value={name}
            onChange={event => setName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                submit();
              }
            }}
            placeholder="e.g. The Morning Show"
            disabled={isSaving}
            className={controlClass}
          />
        </div>

        <div>
          <label htmlFor="project-description" className="mb-1 block text-xs font-medium text-gray-300">
            Description <span className="font-normal text-gray-600">(optional)</span>
          </label>
          <textarea
            id="project-description"
            value={description}
            onChange={event => setDescription(event.target.value)}
            placeholder="What this project is for"
            rows={3}
            disabled={isSaving}
            className={`${controlClass} resize-none`}
          />
        </div>
      </div>
    </Modal>
  );
};

export default ProjectFormModal;
